import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";
import { logger } from "@/lib/logger";
import { ArrowLeft, ArrowRight } from "lucide-react";

// Quiz answers are transient: they only need to survive a page refresh
// during the quiz session. sessionStorage auto-clears when the tab closes,
// giving the answers a shorter blast radius than localStorage.
const STORAGE_KEY = "sv_quiz_answers";

// Symbol mark — three teal bars — reused from Home page.
function Mark({ color = "#0F6E6E", size = 22 }) {
  return (
    <svg viewBox="0 0 672 727" width={size} height={(size * 727) / 672} aria-hidden="true">
      <g fill={color}>
        <rect x="36" y="36" width="600" height="135" rx="29.7" />
        <rect x="126" y="296" width="420" height="135" rx="29.7" />
        <rect x="36" y="556" width="600" height="135" rx="29.7" />
      </g>
    </svg>
  );
}

const CITIES = [
  "Mumbai", "New Delhi", "Bengaluru", "Hyderabad", "Chennai", "Kolkata", "Pune",
  "Ahmedabad", "Jaipur", "Lucknow", "Kanpur", "Nagpur", "Indore", "Bhopal",
  "Visakhapatnam", "Patna", "Vadodara", "Ludhiana", "Agra", "Nashik", "Faridabad",
  "Meerut", "Rajkot", "Varanasi", "Amritsar", "Allahabad (Prayagraj)", "Ranchi",
  "Coimbatore", "Gwalior", "Vijayawada", "Jodhpur", "Madurai", "Raipur", "Guwahati",
  "Chandigarh", "Mysuru", "Tiruchirappalli", "Gurugram", "Noida", "Ghaziabad",
  "Thiruvananthapuram", "Kochi", "Bhubaneswar", "Dehradun", "Navi Mumbai", "Thane",
  "Surat", "Srinagar", "Jammu", "Goa (Panaji)", "Pondicherry", "Howrah", "Other",
];

export default function Quiz() {
  const nav = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  // Lazy initial state — reads saved answers from sessionStorage on the
  // FIRST render (not from a later effect). This eliminates the race where
  // the write-back effect fires with the initial `{}` on mount and wipes
  // the just-restored data (previously exposed by React 18 StrictMode
  // double-mount in dev).
  const [answers, setAnswers] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (parseErr) {
      logger.error("Failed to parse saved quiz answers:", parseErr);
      sessionStorage.removeItem(STORAGE_KEY);
      return {};
    }
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [leadStage, setLeadStage] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", city: "" });

  // Mount effect: fetch quiz questions only. `answers` are already hydrated
  // via the lazy initial state above. Empty deps are intentional — `api`
  // and the setState identifiers are stable across renders.
  useEffect(() => {
    api.get("/quiz/questions")
      .then((r) => { setQuestions(r.data.questions); setLoading(false); })
      .catch((e) => { setErr(formatApiError(e.response?.data?.detail) || e.message); setLoading(false); });
  }, []);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
  }, [answers]);

  if (loading) {
    return (
      <div className="qz-page" data-testid="quiz-page">
        <Nav theme="dark" />
        <div className="qz-shell">
          <div className="qz-card"><div className="spinner-teal" data-testid="quiz-loading" /></div>
        </div>
      </div>
    );
  }

  if (err && !questions.length) {
    return (
      <div className="qz-page">
        <Nav theme="dark" />
        <div className="qz-shell">
          <div className="qz-card"><p className="qz-err">{err}</p></div>
        </div>
      </div>
    );
  }

  const total = questions.length;
  const answered = Object.keys(answers).length;
  const progress = leadStage ? 100 : Math.round((answered / total) * 100);
  const q = questions[idx];

  const pick = (optIdx) => {
    const next = { ...answers, [q.id]: optIdx };
    setAnswers(next);
    setTimeout(() => {
      if (idx < total - 1) setIdx(idx + 1);
      else setLeadStage(true);
    }, 220);
  };

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      const orderedAnswers = questions.map((qi) => answers[qi.id] ?? 0);
      const { data } = await api.post("/quiz/submit", {
        answers: orderedAnswers,
        email: lead.email || null,
        name: lead.name || null,
        city: lead.city || null,
      });
      localStorage.removeItem(STORAGE_KEY);   // legacy cleanup from prior localStorage-based version
      sessionStorage.removeItem(STORAGE_KEY);
      nav(`/quiz/results/${data.submission_id}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="qz-page" data-testid="quiz-page">
      <Nav theme="dark" />

      <div aria-hidden="true" className="qz-orb qz-orb-1" />
      <div aria-hidden="true" className="qz-orb qz-orb-2" />

      <div className="qz-shell">
        <div className="qz-eyebrow">
          <Mark color="#4FBEBE" size={18} />
          <span>Sleep Risk Quiz</span>
          <span className="qz-eyebrow-free">FREE · 2 MIN · PRIVATE</span>
        </div>

        <div className="qz-card">
          <div className="qz-prog">
            <div className="qz-prog-fill" style={{ width: `${progress}%` }} data-testid="quiz-progress-bar" />
          </div>

          {!leadStage ? (
            <>
              <div className="qz-meta">
                <span data-testid="quiz-question-counter">Question {idx + 1} of {total}</span>
                {q.section && <span>{q.section}</span>}
              </div>
              <h1 className="qz-q" data-testid="quiz-question-text">{q.text}</h1>
              <div className="qz-opts">
                {q.options.map((opt, i) => {
                  const active = answers[q.id] === i;
                  return (
                    <button
                      key={`${q.id}-${opt}`}
                      onClick={() => pick(i)}
                      className={`qz-opt ${active ? "active" : ""}`}
                      data-testid={`quiz-option-${i}`}
                    >
                      <span className="qz-dot">{active && <span />}</span>
                      <span className="qz-opt-label">{opt}</span>
                    </button>
                  );
                })}
              </div>
              <div className="qz-nav">
                <button
                  className="qz-back"
                  disabled={idx === 0}
                  onClick={() => setIdx(Math.max(0, idx - 1))}
                  data-testid="quiz-back-btn"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <span className="qz-save-note">Answers saved automatically</span>
              </div>
            </>
          ) : (
            <>
              <div className="qz-meta">
                <span>Last step</span>
                <span>Your details</span>
              </div>
              <h1 className="qz-q">Almost done — tell us a bit about yourself so we can personalise your report.</h1>
              <p className="qz-lede">
                Your name, email and city are optional. We use them only to personalise your report and — if you choose — send a copy to your email. We never sell data.
              </p>

              <label className="qz-label">Your name (optional)</label>
              <input
                className="qz-input"
                value={lead.name}
                onChange={(e) => setLead({ ...lead, name: e.target.value })}
                placeholder="e.g. Rahul Mehta"
                data-testid="lead-name"
              />

              <label className="qz-label">Email (optional — to email you a PDF later)</label>
              <input
                className="qz-input"
                value={lead.email}
                onChange={(e) => setLead({ ...lead, email: e.target.value })}
                placeholder="you@example.com"
                type="email"
                data-testid="lead-email"
              />

              <label className="qz-label">City (helps match you with nearby specialists)</label>
              <select
                className="qz-input"
                value={lead.city}
                onChange={(e) => setLead({ ...lead, city: e.target.value })}
                data-testid="lead-city"
              >
                <option value="">Select your city</option>
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>

              {err && <p className="qz-err">{err}</p>}

              <div className="qz-submit-row">
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="qz-btn-teal"
                  data-testid="submit-quiz-btn"
                >
                  {submitting ? "Calculating…" : "Show my result"} <ArrowRight size={16} />
                </button>
                <button
                  className="qz-back"
                  onClick={() => setLeadStage(false)}
                  data-testid="lead-back-btn"
                >
                  <ArrowLeft size={14} /> Back to questions
                </button>
              </div>
            </>
          )}
        </div>

        <div className="qz-foot">
          <span>Screening based on validated STOP-BANG + Epworth instruments.</span>
          <span>Not a diagnosis.</span>
        </div>
      </div>
    </div>
  );
}
